import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { codexApi } from '../api';
import { celebrate, bigCelebrate } from '../../lib/celebrate';
import * as Icons from 'lucide-react';
import { ChevronLeft, ChevronRight, Check, X, Sparkles } from 'lucide-react';

function ChamberIcon({ name, size = 28, color }) {
  const Cmp = Icons[name] || Icons.Sparkles;
  return <Cmp size={size} color={color} />;
}

export default function CodexChamberDetail() {
  const { chamberId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const d = await codexApi.getChamber(chamberId);
      setData(d);
      // Jump to first incomplete lesson
      const next = d.lessons.findIndex((l) => !l.completed);
      setActiveIndex(next === -1 ? 0 : next);
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [chamberId]);

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <p style={{ color: 'salmon' }}>{error}</p>
        <button onClick={() => navigate('/codex/chambers')} className="codex-btn codex-btn-ghost" style={{ marginTop: 16 }}>
          <ChevronLeft size={14} /> Back to chambers
        </button>
      </div>
    );
  }
  if (!data) return <div style={{ padding: 40, textAlign: 'center', opacity: 0.7 }}>Opening the chamber…</div>;

  const lesson = data.lessons[activeIndex];
  const submit = async () => {
    if (selected == null || submitting) return;
    setSubmitting(true);
    try {
      const res = await codexApi.submitChamberAnswer(chamberId, lesson.id, selected);
      setFeedback(res);
      if (res.correct) {
        if (res.chamber_complete) bigCelebrate();
        else celebrate(0.55);
      }
    } catch (e) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const nextLesson = async () => {
    setSelected(null);
    setFeedback(null);
    await load();
    if (feedback?.next_lesson_index) {
      setActiveIndex(feedback.next_lesson_index - 1);
    } else if (activeIndex < data.lessons.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };

  return (
    <div data-testid="codex-chamber-detail" style={{ paddingTop: 8 }}>
      <Link to="/codex/chambers" className="codex-btn codex-btn-ghost" style={{ marginBottom: 16, display: 'inline-flex' }}>
        <ChevronLeft size={14} /> All chambers
      </Link>

      {/* Chamber header */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="codex-card-gold codex-card"
        style={{
          padding: 24,
          marginBottom: 18,
          background: `linear-gradient(180deg, ${data.accent}33, rgba(168,85,247,0.08))`,
          borderColor: `${data.accent}66`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: `linear-gradient(135deg, ${data.accent}, ${data.accent}aa)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1a0e2c',
          }}>
            <ChamberIcon name={data.icon} size={28} />
          </div>
          <div>
            <div className="codex-eyebrow">CHAMBER {String(data.completed_count).padStart(2, '0')}/{String(data.total_count).padStart(2, '0')}</div>
            <h1 className="codex-h1" style={{ fontSize: '2rem', marginTop: 4 }}>{data.name}</h1>
          </div>
        </div>
        <p style={{ marginTop: 14, lineHeight: 1.7, opacity: 0.9 }}>{data.long_desc}</p>
      </motion.div>

      {/* Lesson tabs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18 }}>
        {data.lessons.map((l, i) => (
          <button
            key={l.id}
            onClick={() => { setActiveIndex(i); setSelected(null); setFeedback(null); }}
            data-testid={`codex-chamber-lesson-tab-${i}`}
            className="codex-card"
            style={{
              padding: '7px 12px',
              fontSize: 12,
              cursor: 'pointer',
              color: 'var(--codex-parchment)',
              borderColor: activeIndex === i ? `${data.accent}aa` : undefined,
              background: activeIndex === i ? `${data.accent}22` : (l.completed ? 'rgba(34,197,94,0.08)' : undefined),
            }}
          >
            <span style={{
              width: 18, height: 18, borderRadius: '50%',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, marginRight: 8,
              background: l.completed ? '#22c55e' : 'transparent',
              border: l.completed ? 'none' : `1px solid ${data.accent}`,
              color: l.completed ? '#1a0e2c' : data.accent,
            }}>
              {l.completed ? <Check size={11} /> : i + 1}
            </span>
            {l.title.split(' — ')[0]}
          </button>
        ))}
      </div>

      {/* Lesson body */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${activeIndex}-${feedback ? 'fb' : 'q'}`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          className="codex-card"
          style={{ padding: 26 }}
          data-testid="codex-chamber-lesson"
        >
          <div className="codex-eyebrow">LESSON {activeIndex + 1}</div>
          <h2 className="codex-h2" style={{ marginTop: 6, color: 'var(--codex-gold-light)' }}>{lesson.title}</h2>

          {!feedback ? (
            <>
              <p style={{ marginTop: 12, lineHeight: 1.75, fontSize: 15 }}>{lesson.intro}</p>

              {lesson.key_points?.length > 0 && (
                <ul style={{ marginTop: 14, paddingLeft: 0, listStyle: 'none' }}>
                  {lesson.key_points.map((kp, i) => (
                    <li key={i} style={{ display: 'flex', gap: 10, marginBottom: 8, fontSize: 14 }}>
                      <span style={{ color: data.accent, fontWeight: 700 }}>✦</span>
                      <span style={{ opacity: 0.9 }}>{kp}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="codex-card" style={{ padding: 18, marginTop: 18, background: 'rgba(0,0,0,0.2)' }}>
                <div className="codex-eyebrow">QUIZ</div>
                <p style={{ marginTop: 8, lineHeight: 1.6 }}>{lesson.quiz_prompt}</p>
                <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
                  {lesson.quiz_options.map((opt, i) => {
                    const isSel = selected === i;
                    return (
                      <motion.button
                        key={i}
                        whileHover={{ x: 4 }}
                        onClick={() => setSelected(i)}
                        data-testid={`codex-chamber-option-${i}`}
                        className="codex-card"
                        style={{
                          padding: 12,
                          textAlign: 'left',
                          cursor: 'pointer',
                          borderColor: isSel ? `${data.accent}aa` : undefined,
                          background: isSel ? `${data.accent}22` : undefined,
                          color: 'var(--codex-parchment)',
                          display: 'flex', alignItems: 'center', gap: 10,
                        }}
                      >
                        <span style={{
                          width: 22, height: 22, borderRadius: 5,
                          border: `1px solid ${data.accent}`,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          background: isSel ? data.accent : 'transparent',
                          color: isSel ? '#1a0e2c' : data.accent,
                          fontSize: 11, fontWeight: 700,
                        }}>{String.fromCharCode(65 + i)}</span>
                        <span style={{ fontSize: 14 }}>{opt}</span>
                      </motion.button>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                  <button
                    disabled={selected == null || submitting || lesson.completed}
                    onClick={submit}
                    className="codex-btn codex-btn-primary"
                    style={{ opacity: (selected != null && !submitting && !lesson.completed) ? 1 : 0.4 }}
                    data-testid="codex-chamber-submit"
                  >
                    {submitting ? 'Sealing…' : lesson.completed ? 'Already complete' : 'Submit'} <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0' }}>
                {feedback.correct ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#7ee2a3' }}><Check size={20} /> <strong>Correct</strong></span>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#f87171' }}><X size={20} /> <strong>Not quite</strong></span>
                )}
                {feedback.xp_gained > 0 && (
                  <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--codex-gold-light)' }}>
                    <Sparkles size={14} /> +{feedback.xp_gained} XP
                  </span>
                )}
              </div>
              <p style={{ lineHeight: 1.75 }}>{feedback.explanation}</p>

              {feedback.chamber_complete && (
                <div className="codex-card-gold" style={{ padding: 14, marginTop: 16, borderRadius: 12 }}>
                  <strong className="codex-display" style={{ color: 'var(--codex-gold)' }}>🏛 Chamber Sealed</strong>{' '}
                  You have completed every lesson in this chamber. The mystery is hidden in your heart now.
                </div>
              )}

              <div style={{ marginTop: 22, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <button onClick={() => { setFeedback(null); setSelected(null); }} className="codex-btn codex-btn-ghost">
                  Read again
                </button>
                {feedback.correct ? (
                  feedback.chamber_complete ? (
                    <button onClick={() => navigate('/codex/chambers')} className="codex-btn codex-btn-primary">
                      <Sparkles size={16} /> Return to the Vault
                    </button>
                  ) : (
                    <button onClick={nextLesson} className="codex-btn codex-btn-primary" data-testid="codex-chamber-next-lesson">
                      Next Lesson <ChevronRight size={16} />
                    </button>
                  )
                ) : (
                  <button onClick={() => { setFeedback(null); setSelected(null); }} className="codex-btn codex-btn-primary">
                    Try Again
                  </button>
                )}
              </div>
            </>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
